// useAudienceSettings — shared hook for reading feature flags per cohort.
//
// The cohort is determined by the authed user's `view_preference` field
// (set via /me/view-preference). Guests default to "tourist" unless they
// stored a manual override in localStorage.habano.viewPref.
import { useEffect, useState } from "react";
import { api, getMe } from "./api";

// Defaults mirror backend.routes.audience_settings.LOCAL_OFF_BY_DEFAULT so the
// UI never blanks while the first fetch is in flight.
const LOCAL_DEFAULTS_OFF = new Set([
  "journey.hotel_card",
  "journey.save_hotel",
  "journey.airport_qa",
  "airport.page",
]);

let _cache = null;
let _cacheAt = 0;
const TTL_MS = 60_000; // refresh at most every 60s per spec

async function fetchSettings() {
  if (_cache && Date.now() - _cacheAt < TTL_MS) return _cache;
  try {
    const { data } = await api.get("/audience-settings");
    _cache = data.settings || {};
    _cacheAt = Date.now();
    return _cache;
  } catch {
    return _cache || {};
  }
}

export function useAudienceSettings() {
  const [cohort, setCohort] = useState(() => {
    try { return localStorage.getItem("habano.viewPref.override") || "tourist"; } catch { return "tourist"; }
  });
  const [settings, setSettings] = useState(_cache || {});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await getMe().catch(() => null);
      if (cancelled) return;
      const override = (() => { try { return localStorage.getItem("habano.viewPref.override"); } catch { return null; } })();
      if (override === "tourist" || override === "local") {
        setCohort(override);
      } else if (me?.view_preference === "local" || me?.view_preference === "tourist") {
        setCohort(me.view_preference);
      }
      const s = await fetchSettings();
      if (!cancelled) setSettings(s);
    })();
    return () => { cancelled = true; };
  }, []);

  const isEnabled = (featureKey) => {
    const row = settings[featureKey];
    if (!row) {
      // Fallback to defaults if no row yet
      if (cohort === "local" && LOCAL_DEFAULTS_OFF.has(featureKey)) return false;
      return true;
    }
    return !!row[cohort];
  };

  return { cohort, setCohort, isEnabled, settings };
}

export function setAudienceOverride(view) {
  try {
    if (view === "tourist" || view === "local") {
      localStorage.setItem("habano.viewPref.override", view);
    } else {
      localStorage.removeItem("habano.viewPref.override");
    }
  } catch { /* ignore */ }
}
